import axios from 'axios';
import storage from 'conversional-persistent-storage';
import env from '@/constants/environment';
import {
  HELP_ARTICLES,
  HELP_CATEGORIES,
  HELP_FAQS,
  HELP_ROOT,
  MEILISEARCH_MULTI_SEARCH,
  MEILISEARCH_ROOT,
} from '@/constants/helpCenterPaths';

declare const BACKOFFICE_ENV: string | undefined;

const STRAPI_TOKEN = 'c82a15b16c0439b4042b63593d1dda73f4caa538ec8ef9f240e4b3f64dcc8016f6754f3b9b34b7a5faa4ed7a79fbdc079186c27d5dfeeb44b875df0d90fa9a9c847f61353ba88ce503d178ba96dbe92f8b8cb0ec22cb9a95d39a428b4c7dea685c19712a2987f5b1e2319ce75031d40cfa154eb383795e99b428181b61c35dc2';
const MEILISEARCH_TOKEN = 'cf0527dfd3205448b42fd6f11adbe06639cee6667deb89edb1971e2a025a8d0b';

// Dev builds can read a local Strapi instead: localStorage helpCmsUrl (e.g. http://localhost:1337/api) + helpCmsToken.
const devOverride = (key: string) =>
  env.ENV === 'development' ? storage.getItem(key, undefined, 'localStorage') : undefined;

const apiClient = axios.create({
  baseURL: devOverride('helpCmsUrl') || HELP_ROOT,
  timeout: 10000,
  headers: {
    Authorization: `Bearer ${devOverride('helpCmsToken') || STRAPI_TOKEN}`,
  },
});

const populateFields = (relation: string, fields: string[]) => ({
  [`populate[${relation}][fields]`]: fields.join(','),
});

export const fetchHelpCategories = async ({ locale }) => {
  const res = await apiClient.get(HELP_CATEGORIES, {
    params: {
      ...populateFields('articles', ['title', 'slug', 'id']),
      'populate[children][populate][articles][fields]': 'id,title,slug',
      'filters[parent][id][$null]': true,
      'pagination[pageSize]': 100,
      ...(locale && { locale }),
    },
  });
  return res.data;
};

export const fetchHelpArticleBySlug = async ({ slug, locale }) => {
  const res = await apiClient.get(HELP_ARTICLES, {
    params: {
      'populate[category][fields]': 'name,slug',
      'populate[category][populate][parent][fields]': 'name,slug',
      "populate[content][populate]": "*",
      'filters[slug][$eq]': slug,
      ...(locale && { locale }),
    },
  });
  return res.data;
};

export const fetchActiveReleaseNote = async ({ locale }) => {
  const res = await apiClient.get(HELP_ARTICLES, {
    params: {
      'filters[articleType][$eq]': 'release_note',
      'filters[expiresAt][$gt]': new Date().toISOString(),
      'populate[content][populate]': '*',
      'populate[coverImage]': true,
      'populate[tourSteps][populate][image]': true,
      'sort': 'publishedAt:desc',
      'pagination[pageSize]': 1,
      ...(locale && { locale }),
      // Testing shares the production help center: it previews the unpublished draft, production shows the published note.
      ...(BACKOFFICE_ENV !== 'production' && { status: 'draft' }),
    },
  });
  return res.data?.data?.[0] || null;
};

export const fetchFeaturedArticles = async ({ locale }) => {
  const res = await apiClient.get(HELP_ARTICLES, {
    params: {
      'populate[category]': true,
      'filters[isFeatured][$eq]': true,
      'pagination[pageSize]': 100,
      'sort': 'title:asc',
      ...(locale && { locale }),
    },
  });
  return res.data;
};

export const fetchFeaturedQuestions = async ({ locale }) => {
  const res = await apiClient.get(HELP_FAQS, {
    params: {
      'filters[isFeatured][$eq]': true,
      'pagination[pageSize]': 100,
      'sort': 'order:asc',
      ...(locale && { locale }),
    },
  });
  return res.data;
};

export const fetchArticlesAndQuestionsByCategorySlug = async ({ slug, locale }) => {
  // Fetch both articles and FAQs for the category
  const results = await Promise.all([
    apiClient.get(HELP_ARTICLES, {
      params: {
        'populate[category]': true,
        'filters[category][slug][$eq]': slug,
        'pagination[pageSize]': 100,
        'sort': 'title:asc',
        ...(locale && { locale }),
      },
    }),
    apiClient.get(HELP_FAQS, {
      params: {
        'populate[category]': true,
        'filters[category][slug][$eq]': slug,
        'pagination[pageSize]': 100,
        'sort': 'order:asc',
        ...(locale && { locale }),
      },
    }),
  ]);

  return results.map(result => result?.data?.data).filter(Boolean)
};

export const fetchAllQuestions = async ({ locale }) => {
  const res = await apiClient.get(HELP_FAQS, {
    params: {
      'populate[category]': true,
      'pagination[pageSize]': 100,
      'sort': 'order:asc',
      ...(locale && { locale }),
    },
  });
  return res.data;
};

// Fetch articles and FAQs by public tag slugs
export const fetchArticlesAndQuestionsByTags = async ({ tags = [], locale }) => {
  const tagList = Array.isArray(tags) ? tags : [tags];

  const results = await Promise.all([
    apiClient.get(HELP_ARTICLES, {
      params: {
        'populate[category]': true,
        // Filter articles by related tag slug(s)
        ...(tagList.length > 0 && { 'filters[tags][slug][$in]': tagList }),
        'pagination[pageSize]': 100,
        ...(locale && { locale }),
      },
    }),
    apiClient.get(HELP_FAQS, {
      params: {
        'populate[category]': true,
        ...(tagList.length > 0 && { 'filters[tags][slug][$in]': tagList }),
        'pagination[pageSize]': 100,
        'sort': 'order:asc',
        ...(locale && { locale }),
      },
    }),
  ]);

  return results.map((r) => r?.data?.data).filter(Boolean);
};

// Fetch articles and FAQs by internal tag keys
export const fetchArticlesAndQuestionsByInternalTags = async ({ internalTags = [], locale }) => {
  const keys = Array.isArray(internalTags) ? internalTags : [internalTags];

  const results = await Promise.all([
    apiClient.get(HELP_ARTICLES, {
      params: {
        'populate[category]': true,
        ...(keys.length > 0 && { 'filters[internalTags][key][$in]': keys }),
        'pagination[pageSize]': 100,
        ...(locale && { locale }),
      },
    }),
    // apiClient.get(HELP_FAQS, {
    //   params: {
    //     'populate[category]': true,
    //     ...(keys.length > 0 && { 'filters[internalTags][key][$in]': keys }),
    //     'pagination[pageSize]': 100,
    //     'sort': 'order:asc',
    //     ...(locale && { locale }),
    //   },
    // }),
  ]);

  return results.map((r) => r?.data?.data).filter(Boolean);
};

export const searchHelpCenter = async ({ query, locale }) => {
  const meilisearchClient = axios.create({
    baseURL: MEILISEARCH_ROOT,
    timeout: 10000,
    headers: {
      Authorization: `Bearer ${MEILISEARCH_TOKEN}`,
    },
  });

  const res = await meilisearchClient.post(MEILISEARCH_MULTI_SEARCH, {
    queries: [
      {
        indexUid: 'help-article',
        q: query,
        limit: 5,
        ...(locale && { filter: `locale = ${locale}` }),
      },
      {
        indexUid: 'faq',
        q: query,
        limit: 5,
        ...(locale && { filter: `locale = ${locale}` }),
      },
    ],
  });

  return res.data;
};
